import React, { useMemo } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { RootState } from "us.helper/types";
import { getStandardRateDetails } from "./Functions";
import { IStandardRateDetails } from "./Interfaces";
import { RateActions } from "us.collection.admin/constants";
import * as Actions from "us.collection.admin/actions";
import { SaveStandardRate } from "./Repository";
import { StandardRateValidationSchema } from "./Validations";

const { $Button, $TextArea, $Form, $Popconfirm, $DatePicker, $InputNumber } =
  Common.Components;

/**
 * @description - Standard Rates details component for add/edt/delete rates.
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3479208007/View+Standard+Rate+UI+Implementation
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 04/26/2024
 */
const StandardRateDetails: React.FC<IStandardRateDetails & PropsFromRedux> = (
  props
) => {
  const { t } = useTranslation();

  const {
    rates,
    drawerInfo,
    currentDateFormat,
    isStandardRateSaving,
    isStandardRateDeleting,
    addStandardRate,
    editStandardRate,
    deleteStandardRate,
    onCancel,
  } = props;

  const { rateId: selectedRateId, type: drawerType } = drawerInfo ?? {};

  const initStandardRateDetails = useMemo(
    () => getStandardRateDetails(rates.data, selectedRateId) ?? {},
    [rates.data, selectedRateId]
  );

  /**
   * @description Handle submit button action
   */
  const handleSubmit = (values: any, actions: any) => {
    if (actions.isValidating) return;

    const saveParams = SaveStandardRate.call({
      ...values,
      rateId: selectedRateId,
    });

    if (drawerType === RateActions.NEW) {
      // add new standard rate
      addStandardRate && addStandardRate({ saveParams });
    } else if (drawerType === RateActions.EDIT) {
      // update existing standard rate
      editStandardRate && editStandardRate({ saveParams });
    } else {
      // delete existing standard rate
      deleteStandardRate && deleteStandardRate({ ...saveParams });
    }
    actions.setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initStandardRateDetails}
      enableReinitialize
      validateOnChange
      validateOnBlur
      validationSchema={StandardRateValidationSchema(
        rates.data,
        drawerType,
        initStandardRateDetails
      )}
      onSubmit={handleSubmit}
    >
      {({ values, handleSubmit, isSubmitting, isValid }: any) => (
        <>
          <$Form layout="vertical">
            <div className="mb-3">
              <$InputNumber
                formItem={{
                  label: t("US.COLLECTION.ADMIN:STANDARD_RATE.RATE"),
                  required: true,
                }}
                addonAfter="%"
                placeholder="0.00"
                className="w-100"
                size="small"
                type="number"
                min={0}
                max={100}
                value={values.rate}
                name="rate"
                disabled={drawerType == RateActions.DELETE}
              />
            </div>
            <$DatePicker
              required
              label={t("US.COLLECTION.ADMIN:STANDARD_RATE.EFFECTIVE_DATE")}
              size="small"
              name="effectiveDate"
              className="w-100 mb-3"
              value={values.effectiveDate}
              disabled={drawerType == RateActions.DELETE}
              placeholder={currentDateFormat}
              format={currentDateFormat}
            />
            <$TextArea
              data-testid="comment"
              className="w-100"
              label={t("US.COLLECTION.ADMIN:STANDARD_RATE.COMMENT")}
              name="comment"
              maxLength={500}
              autoSize={{ minRows: 4, maxRows: 6 }}
              required={drawerType != RateActions.NEW ? true : undefined}
            />
          </$Form>
          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Popconfirm
                title={t("COMMON.SURE_TO_PROCEED_?")}
                placement="topLeft"
                data-testid="pop-confirm"
                okButtonProps={{
                  dataTestid: "pop-confirm-ok",
                }}
                onConfirm={() => handleSubmit()}
                okText={t("COMMON.YES")}
                cancelText={t("COMMON.NO")}
                disabled={
                  !isValid ||
                  isSubmitting ||
                  isStandardRateSaving ||
                  isStandardRateDeleting
                }
              >
                {drawerType != RateActions.DELETE && (
                  <$Button
                    className="ml-3 mr-2"
                    type="primary"
                    data-testid="save-btn"
                    loading={isSubmitting || isStandardRateSaving}
                    disabled={!isValid || isSubmitting || isStandardRateSaving}
                  >
                    {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                  </$Button>
                )}
                {drawerType == RateActions.DELETE && (
                  <$Button
                    className="ml-3 mr-2"
                    type="primary"
                    data-testid="save-btn"
                    danger
                    loading={isSubmitting || isStandardRateDeleting}
                    disabled={
                      !isValid || isSubmitting || isStandardRateDeleting
                    }
                  >
                    {t("US.COLLECTION.COMMON:COMMON.DELETE")}
                  </$Button>
                )}
              </$Popconfirm>
              <$Button
                data-testid="cancel-item-type"
                onClick={() => onCancel()}
              >
                {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
              </$Button>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { standardRates, common } = state;
  const { isStandardRateSaving, isStandardRateDeleting, rates, drawerInfo } =
    standardRates;
  const { currentDateFormat } = common;

  return {
    rates,
    drawerInfo,
    currentDateFormat,
    isStandardRateSaving,
    isStandardRateDeleting,
  };
};

const { addStandardRate, editStandardRate, init } = Actions.standardRates;

const mapDispatchToProps = {
  addStandardRate: addStandardRate.save,
  editStandardRate: editStandardRate.save,
  deleteStandardRate: init.delete,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(StandardRateDetails);
